import _ from 'lodash';

import { AiProcessOwnerKind } from 'constants/aiProcessRuns';
import { HTTP_RESPONSE_STATUS_CODES, SKIP_FORBIDDEN_RESPONSE_CONFIG } from 'constants/http';

const OWNER_ROUTE_SEGMENT = {
  [AiProcessOwnerKind.LOAN_APPLICATION]: 'loan-applications',
  [AiProcessOwnerKind.SUB_COMPANY]: 'sub-companies',
  [AiProcessOwnerKind.RATE_SHEET_UPLOAD]: 'price-source-uploads',
};

const resolveOwnerSegment = ownerKind => {
  const segment = OWNER_ROUTE_SEGMENT[ownerKind];

  if (!segment) {
    throw new Error(`[AiProcessRunsApi] Unsupported owner kind: ${ownerKind}`);
  }

  return segment;
};

export class AiProcessRunsApi {
  #http;

  constructor(http) {
    this.#http = http;
  }

  async getRunsForOwner({
    ownerKind,
    ownerId,
    status,
    processId,
    startedAfter,
    startedBefore,
    skip,
    take,
  }) {
    const params = {};

    if (status) {
      params.status = status;
    }

    if (processId) {
      params.processId = processId;
    }

    if (startedAfter) {
      params.startedAfter = startedAfter;
    }

    if (startedBefore) {
      params.startedBefore = startedBefore;
    }

    if (_.isNumber(skip)) {
      params.skip = skip;
    }

    if (_.isNumber(take)) {
      params.take = take;
    }

    const segment = resolveOwnerSegment(ownerKind);

    return this.#http.get(`/${segment}/${ownerId}/ai-process-executions`, { params });
  }

  async getProcessesUsedByOwner({ ownerKind, ownerId }) {
    const segment = resolveOwnerSegment(ownerKind);

    return this.#http.get(`/${segment}/${ownerId}/ai-processes`);
  }

  async getRunDetail({ executionId }) {
    return this.#http.get(`/ai-process-executions/${executionId}`);
  }

  async getRunSteps({ executionId }) {
    return this.#http.get(`/ai-process-executions/${executionId}/steps`);
  }

  async getStepMessages({ stepExecutionId }) {
    return this.#http.get(`/ai-process-step-executions/${stepExecutionId}/messages`);
  }

  async getStepCitations({ stepExecutionId }) {
    // A step with no guideline citations answers 404 (not 204). Accept it as a
    // valid "no citations" response so the thunk resolves to an empty list
    // instead of routing through the error toast. A 403 stays inline (LEN-213):
    // the citation cards degrade to "unavailable" instead of a page-level toast.
    return this.#http.get(`/ai-process-step-executions/${stepExecutionId}/citations`, {
      ...SKIP_FORBIDDEN_RESPONSE_CONFIG,
      validateStatus: status =>
        status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ||
        status === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND,
    });
  }

  // LEN-213: every guideline citation the run references (step outputs + review-form
  // values), resolved server-side. 404 (an API that predates the endpoint) resolves
  // to "no citations"; a 403 stays inline so the chips degrade quietly.
  async getRunCitations({ executionId }) {
    return this.#http.get(`/ai-process-executions/${executionId}/citations`, {
      ...SKIP_FORBIDDEN_RESPONSE_CONFIG,
      validateStatus: status =>
        status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ||
        status === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND,
    });
  }

  async getStepBlob({ stepExecutionId, kind }) {
    return this.#http.get(`/ai-process-step-executions/${stepExecutionId}/blobs/${kind}`, {
      validateStatus: status =>
        status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ||
        status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT,
    });
  }

  async getStepPendingToolCall({ stepExecutionId }) {
    return this.#http.get(`/ai-process-step-executions/${stepExecutionId}/pending-tool-call`, {
      validateStatus: status =>
        status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ||
        status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT,
    });
  }

  async startExecution({ processId, entityId, triggerSource, subjectCertificationIds }) {
    return this.#http.post(`/ai-processes/${processId}/execute`, {
      entityId,
      triggerSource: triggerSource ?? null,
      // DEV-3046: 0..N certification subjects for SubCompany-scoped runs (empty/omitted
      // ⇒ holistic). The server derives the sub-company anchor from the cert set.
      subjectCertificationIds: subjectCertificationIds?.length ? subjectCertificationIds : null,
    });
  }

  async confirmStepExecution({
    stepExecutionId,
    decision,
    reason,
    selectedSlaGroupId,
    conditionDecisions,
  }) {
    return this.#http.post(`/ai-process-step-executions/${stepExecutionId}/confirm`, {
      decision,
      reason: reason ?? null,
      selectedSlaGroupId: selectedSlaGroupId ?? null,
      conditionDecisions: conditionDecisions ?? null,
    });
  }

  async retryStepExecution({ stepExecutionId }) {
    return this.#http.post(`/ai-process-step-executions/${stepExecutionId}/retry`);
  }

  async retryStepFromHereExecution({ stepExecutionId }) {
    return this.#http.post(`/ai-process-step-executions/${stepExecutionId}/retry-from-here`);
  }

  async getExecutionForm({ executionId }) {
    return this.#http.get(`/ai-process-executions/${executionId}/form`, {
      validateStatus: status =>
        status === HTTP_RESPONSE_STATUS_CODES.SUCCESS ||
        status === HTTP_RESPONSE_STATUS_CODES.NO_CONTENT,
    });
  }

  async reviewFormField({ formId, fieldKey, reviewState, overriddenValue }) {
    return this.#http.post(`/ai-forms/${formId}/fields/${fieldKey}/review`, {
      reviewState,
      overriddenValue: overriddenValue ?? null,
    });
  }

  async reviewFormFields({ formId, reviews }) {
    return this.#http.post(`/ai-forms/${formId}/fields/review`, { verdicts: reviews });
  }

  async transitionForm({ formId, action }) {
    return this.#http.post(`/ai-forms/${formId}/transition`, { action });
  }
}
